import { useCallback } from 'react';

import useToggle from '@lyearn/core/hooks/useToggle';
import { stopEventPropagation } from '@lyearn/core/utils/events';

interface Params<T> {
  key: T;
  draggingItemRef: React.MutableRefObject<T | undefined>;
  onDrop: (key: T) => void;
  disabled?: boolean;
}

const useDraggableItem = <T>({ key, draggingItemRef, onDrop, disabled }: Params<T>) => {
  const [draggable, { on, off }] = useToggle(false);

  const getDragHandleProps = useCallback(() => {
    if (disabled) {
      return {};
    }

    return {
      onMouseEnter: on,
      onMouseLeave: off,
    };
  }, [disabled, off, on]);

  const getDropOverContainerProps = useCallback(
    () => ({
      draggable,
      onDragEnd: () => {
        draggingItemRef.current = undefined;
      },
      onDragStart: (event: React.DragEvent<HTMLDivElement>) => {
        draggingItemRef.current = key;
        stopEventPropagation(event);
      },
      onDrop: () => {
        onDrop(key);
      },
    }),
    [draggable, draggingItemRef, key, onDrop],
  );

  return { draggable, getDropOverContainerProps, getDragHandleProps };
};

export default useDraggableItem;
